"use client";

import { useState, useEffect } from "react";
import { VSL_CONFIG, QUIZ_TIMERS } from "@/config/constants";
import { VturbPlayer } from "@/components/VturbPlayer";
import { Button } from "@/components/ui/button";

type VSLVersion = "marlon" | "camilla";

interface Hero8PilaresProps {
  onCtaClick?: () => void;
}

export const Hero8Pilares = ({ onCtaClick }: Hero8PilaresProps) => {
  const [mounted, setMounted] = useState(false);
  const [vslVersion, setVslVersion] = useState<VSLVersion>("marlon");
  const [showButton, setShowButton] = useState(false);

  const vslConfig = VSL_CONFIG.pilares8;

  useEffect(() => {
    setMounted(true);
    const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (mq?.matches) {
      document.documentElement.classList.add("reduced-motion");
    }

    // Verificar query parameter
    const searchParams = new URLSearchParams(window.location.search);
    const vslParam = searchParams.get("vsl") as VSLVersion | null;
    if (vslParam && (vslParam === "marlon" || vslParam === "camilla")) {
      setVslVersion(vslParam);
    }
  }, []);

  // Timer para mostrar botão
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowButton(true);
    }, QUIZ_TIMERS.product_page_8pilares);
    return () => clearTimeout(timer);
  }, []);

  if (!mounted) return null;

  return (
    <section
      className="relative flex overflow-hidden bg-gradient-to-b from-slate-950 via-slate-900/95 to-background"
      aria-label="Seção principal com VSL do Método LDA"
    >
      {/* Background animado + vinheta */}
      <div className="pointer-events-none absolute inset-0">
        {/* Gradiente radial forte */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/20 via-transparent to-transparent" aria-hidden />
        
        {/* Blobs animados */}
        <div
          className="absolute top-20 left-20 w-96 h-96 bg-primary/30 rounded-full blur-[120px] motion-safe:animate-glow-pulse reduced-motion:opacity-75"
          aria-hidden
        />
        <div
          className="absolute bottom-20 right-20 w-96 h-96 bg-accent/20 rounded-full blur-[120px] motion-safe:animate-glow-pulse reduced-motion:opacity-75"
          style={{ animationDelay: "2s" }}
          aria-hidden
        />
        
        {/* Vinheta superior e inferior */}
        <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-background/50 to-transparent" aria-hidden />
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-background to-transparent" aria-hidden />
      </div>

      {/* Conteúdo central */}
      <div className="relative z-10 w-full px-4 sm:px-6 lg:px-8 py-4 sm:py-20 lg:py-24">
        <div className="mx-auto max-w-7xl flex flex-col items-center gap-10 sm:gap-12 lg:gap-14">

          {/* VSL: ConvertKit Player */}
          <div className="relative w-full px-0">
            {/* Glow de fundo do card */}
            <div
              className="pointer-events-none absolute inset-0 -z-10 bg-gradient-radial from-primary/40 via-accent/20 to-transparent blur-3xl"
              aria-hidden
            />

            <div className="mx-auto w-full max-w-sm sm:max-w-md group motion-safe:animate-scale-in">
              <div className="relative rounded-2xl sm:rounded-3xl p-1 sm:p-2">
                <VturbPlayer
                  key={vslVersion}
                  videoId={vslConfig[vslVersion].id}
                  scriptUrl={vslConfig[vslVersion].url}
                  className="relative w-full rounded-xl sm:rounded-2xl overflow-hidden"
                />
              </div>

              {/* Glow ao redor no hover */}
              <div className="pointer-events-none absolute -inset-6 -z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-radial from-primary/30 via-accent/15 to-transparent blur-3xl" />
            </div>

            {/* CTA + Microcopy de segurança */}
            {showButton && (
              <div className="mt-10 flex flex-col items-center gap-4 animate-fade-in">
                <Button
                  variant="hero"
                  size="xl"
                  onClick={onCtaClick}
                  className="px-12 sm:px-16 py-6 sm:py-7 text-lg sm:text-xl font-bold shadow-2xl shadow-green-500/30 hover:shadow-green-400/50 transition-all hover:-translate-y-1 w-full sm:w-auto"
                >
                  🚀 QUERO COMEÇAR A JORNADA
                </Button>
                <p className="text-sm text-slate-400 flex items-center gap-2">
                  <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Acesso imediato • Garantia de 7 dias
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
